import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Edit, Image as ImageIcon, Info } from "lucide-react";
import { ImageUploader } from "./ImageUploader";
import { cn } from "@/lib/utils";

interface VisualImageEditableProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    isEditMode?: boolean;
    onImageChange?: (url: string) => void;
    guidelines?: {
        width?: string;
        height?: string;
        description?: string;
        recommendedSize?: string;
    };
    containerClassName?: string;
    subdomain?: string;
    fallback?: React.ReactNode;
}

export const VisualImageEditable: React.FC<VisualImageEditableProps> = ({ 
    src, 
    isEditMode = false, 
    onImageChange, 
    guidelines,
    className,
    containerClassName,
    alt = "Editable image",
    subdomain,
    fallback,
    ...props
}) => {
    const [open, setOpen] = useState(false);

    // If not in edit mode, just render the image natively
    if (!isEditMode) {
        if (!src && fallback) return <>{fallback}</>;
        if (!src) return null; // Or some default empty state
        return <img src={src} className={className} alt={alt} {...props} />;
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <div className={cn("relative group cursor-pointer border-2 border-transparent hover:border-purple-500 transition-all rounded-sm w-full h-full", containerClassName)}>
                {/* The Image */}
                <PopoverTrigger asChild>
                    <div className="relative w-full h-full">
                        {src ? (
                            <img src={src} className={cn("w-full h-full", className)} alt={alt} {...props} />
                        ) : (
                            <div className={cn("w-full h-full flex items-center justify-center bg-slate-100 text-slate-400", className)}>
                                {fallback || <ImageIcon size={24} />}
                            </div>
                        )}
                        
                        {/* Overlay on Hover (Always visible if no image) */}
                        <div className={cn(
                            "absolute inset-0 bg-purple-900/40 transition-opacity flex items-center justify-center pointer-events-none",
                            src ? "opacity-0 group-hover:opacity-100" : "opacity-100"
                        )}>
                            <div className="bg-purple-600 text-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2 font-bold text-sm transform translate-y-2 group-hover:translate-y-0 transition-transform">
                                <Edit size={14} /> {src ? "Change Image" : "Add Image"}
                            </div>
                        </div>
                        
                        {/* Always visible dashed helper border in edit mode */}
                        <div className="absolute inset-0 border-2 border-dashed border-purple-300 opacity-0 group-hover:opacity-50 pointer-events-none" />
                    </div>
                </PopoverTrigger>

                <PopoverContent className="w-80 p-4" sideOffset={10}>
                    <div className="space-y-4">
                        <div className="border-b pb-2 mb-2">
                             <h4 className="font-bold text-slate-900 flex items-center gap-2">
                                <ImageIcon size={18} className="text-purple-600" />
                                Edit Image
                            </h4>
                            {guidelines && (
                                <div className="mt-2 text-xs text-slate-500 bg-slate-50 p-2 rounded flex gap-2 items-start">
                                    <Info size={14} className="mt-0.5 shrink-0 text-blue-500" />
                                    <div>
                                        {guidelines.description && <p className="mb-1 text-slate-700">{guidelines.description}</p>}
                                        {guidelines.recommendedSize && <p><span className="font-semibold">Size:</span> {guidelines.recommendedSize}</p>}
                                    </div>
                                </div>
                            )}
                        </div>

                        {onImageChange ? (
                             <ImageUploader 
                                value={src} 
                                onChange={(url) => {
                                    onImageChange(url);
                                    // Don't close immediately so user can see preview
                                }}
                                recommendedSize={guidelines?.recommendedSize}
                                subdomain={subdomain}
                             />
                        ) : (
                            <p className="text-red-500 text-sm">No update handler provided.</p>
                        )}
                        
                        <div className="flex justify-end pt-2">
                            <Button size="sm" onClick={() => setOpen(false)}>Done</Button>
                        </div>
                    </div>
                </PopoverContent>
            </div>
        </Popover>
    );
};
